import React, { useEffect, useMemo, useState } from 'react';
import { getFeaturedStrip, setFeaturedStrip, removeFeaturedStrip, getPageSummaries } from '../api';
import Alert from '../ui/Alert';
import Badge from '../ui/Badge';
import Spinner from '../ui/Spinner';
import ConfirmDialog from '../ui/ConfirmDialog';
import { TEXT_MAX, pagePath, toPath, stripProblem, pickablePages } from './featuredStrip';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const OTHER = '__other__';

/* Site settings panel: the one-line "Featured" strip nfw shows at the top of every
   page (domain field skyscraper = {text, link}). Saving schedules an edge purge for
   after the sites' 5-minute domain cache, so the panel says when it will show. */
export default function FeaturedStripSettings({ domainId, host, liveUrl, onChange }) {
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [custom, setCustom] = useState(false);   // "Another path…" chosen, path box open

  useEffect(() => {
    let cancelled = false;
    Promise.all([getFeaturedStrip(domainId), getPageSummaries(domainId).catch(() => [])])
      .then(([strip, pageList]) => {
        if (cancelled) return;
        setData(strip);
        setPages(pickablePages(pageList));
        setText(strip.skyscraper?.text || '');
        setLink(strip.skyscraper?.link || '');
      })
      .catch((err) => { if (!cancelled) setError(errText(err, 'Failed to load the featured message')); });
    return () => { cancelled = true; };
  }, [domainId]);

  const saved = data?.skyscraper || null;
  const path = toPath(link, host);
  const problem = stripProblem(text, path);
  const dirty = !saved || saved.text !== text.replace(/\s+/g, ' ').trim() || saved.link !== path;
  const pickerValue = useMemo(() => {
    if (custom) return OTHER;
    if (!path) return '';
    const match = pages.find((p) => pagePath(p) === path);
    return match ? match.id : OTHER;
  }, [custom, pages, path]);

  const after = (strip, message) => {
    setData(strip);
    setText(strip.skyscraper?.text || '');
    setLink(strip.skyscraper?.link || '');
    setCustom(false);
    const [soon, latest] = strip.live_within_minutes || [5, 15];
    setNotice(`${message} The site shows the change within about ${soon} minutes (at most ${latest}).`);
    onChange?.(strip.skyscraper);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      after(await setFeaturedStrip(domainId, text, path), 'Saved.');
    } catch (err) {
      setError(errText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      after(await removeFeaturedStrip(domainId), 'Removed.');
    } catch (err) {
      setError(errText(err, 'Failed to remove'));
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return <Spinner label="Loading featured message…" />;
  if (!data) return <Alert type="error">{error}</Alert>;

  const preview = text.replace(/\s+/g, ' ').trim();
  const linkedPage = data.page;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <p className="muted" style={{ margin: 0 }}>
        A one-line <strong>Featured</strong> strip at the top of every page on the site, linking to one page.
        Readers can close it.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {saved ? <Badge state="success">On</Badge> : <Badge state="info">Off</Badge>}
        {saved ? (
          <span className="small">
            Links to{' '}
            <a href={`${liveUrl}${saved.link}`} target="_blank" rel="noopener noreferrer">
              {linkedPage?.title || saved.link}
            </a>
            {linkedPage && linkedPage.status !== 'published' && (
              <> <Badge state="warn">{linkedPage.status}</Badge></>
            )}
          </span>
        ) : (
          <span className="muted small">No strip is showing on the site.</span>
        )}
      </div>
      {saved && linkedPage && linkedPage.status !== 'published' && (
        <Alert type="warning">The linked page isn&apos;t published, so readers who click the strip will get a 404.</Alert>
      )}
      {saved && !linkedPage && saved.link !== '/' && (
        <Alert type="info">
          {saved.link} isn&apos;t a page in the DAM. That&apos;s fine for a site template page; open it to check it works.
        </Alert>
      )}

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <Alert type="success" onDismiss={() => setNotice(null)}>{notice}</Alert>

      <div className="field">
        <label htmlFor="strip-text">Message</label>
        <input id="strip-text" className="input" value={text} maxLength={TEXT_MAX + 20}
               placeholder="e.g. The best cupcake stands for parties and weddings"
               onChange={(e) => setText(e.target.value)} />
        <span className={preview.length > TEXT_MAX ? 'error' : 'hint'}>{preview.length}/{TEXT_MAX} characters</span>
      </div>

      <div className="field">
        <label htmlFor="strip-page">Links to</label>
        <select id="strip-page" className="select" value={pickerValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustom(v === OTHER);
                  if (v === OTHER) { if (pickerValue !== OTHER) setLink(''); return; }
                  const page = pages.find((p) => p.id === v);
                  setLink(page ? pagePath(page) : '');
                }}>
          <option value="">Choose a page…</option>
          {pages.map((p) => <option key={p.id} value={p.id}>{p.title || p.slug}</option>)}
          <option value={OTHER}>Another path on this site…</option>
        </select>
        {pickerValue === OTHER && (
          <input className="input mono" style={{ marginTop: '6px' }} value={link} aria-label="Path"
                 placeholder="/my-page/ or a full URL on this site" onChange={(e) => setLink(e.target.value)} />
        )}
        {path && <span className="hint mono">{path}</span>}
      </div>

      <div>
        <div className="muted small" style={{ marginBottom: '4px' }}>Preview</div>
        <div style={{ border: '1px solid var(--color-border, #e8e8e8)', borderRadius: '6px', padding: '10px 14px',
                      display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-surface, #fff)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                         color: 'var(--color-primary)' }}>Featured</span>
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {preview || <span className="muted">Your message</span>}
          </strong>
        </div>
      </div>

      {problem && (text || link) && <span className="error" role="alert">{problem}</span>}
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !!problem || !dirty}>
          {saving ? 'Saving…' : saved ? 'Save changes' : 'Show on site'}
        </button>
        {saved && (
          <button type="button" className="btn btn-secondary" onClick={() => setConfirmRemove(true)} disabled={saving}>
            Remove strip
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove the featured message?"
        message="The strip comes off every page on the site within a few minutes. The linked page itself is not changed."
        confirmLabel="Remove"
        danger
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
